import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

/**
 * Visual refresh (post-migration, see Button.tsx) onto the shadcn-shaped
 * tokens in theme.css — rounded-xl/border-border/bg-card resolve to the same
 * underlying runtime-branded values as the original rounded-card-lg/
 * border-line/bg-surface aliases, just in the vocabulary the ported PulseOS
 * components (Dialog, Table, ...) already use, so a Card and a DialogContent
 * sitting in the same view agree on radius and surface color.
 */
export const Card = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'rounded-xl border border-border bg-card p-5 shadow-card',
        className
      )}
      {...props}
    />
  )
);
Card.displayName = 'Card';

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    // flex-wrap so a title + action buttons that don't fit on one line (a
    // narrow sidebar/rail, not just a phone) wrap onto a second line instead
    // of overflowing past the card's border.
    <div ref={ref} className={cn('mb-3.5 flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5', className)} {...props} />
  )
);
CardHeader.displayName = 'CardHeader';

export const CardTitle = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center gap-2 text-sm font-semibold', className)} {...props} />
  )
);
CardTitle.displayName = 'CardTitle';

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn(className)} {...props} />
);
CardContent.displayName = 'CardContent';

/**
 * Reproduces .stat-card. `accentColor` is an inline style, not a Tailwind
 * class, because it varies per-instance with a value that isn't known until
 * render (a status colour picked by the caller) — Tailwind's static scanner
 * can't pick up a class built from a runtime string, and the legacy
 * QueueView.jsx this replaces used the same inline-style approach for the
 * identical reason. Every other property is a utility class.
 *
 * `accentSide` defaults to 'top' (QueueView.jsx's treatment, the first
 * consumer) — 'left' was added once a second, genuinely different legacy
 * screen needed it (ReportsModule.jsx's stat cards use borderLeft, not
 * borderTop; not a mistake to reconcile, both are real, deliberate legacy
 * choices this component now expresses rather than forcing one screen to
 * match the other's look).
 */
export interface StatCardProps extends HTMLAttributes<HTMLDivElement> {
  accentColor?: string;
  accentSide?: 'top' | 'left';
}

export const StatCard = forwardRef<HTMLDivElement, StatCardProps>(
  ({ className, accentColor, accentSide = 'top', style, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('rounded-xl border border-border bg-card px-5 py-4 shadow-card', className)}
      style={
        accentColor
          ? { [accentSide === 'left' ? 'borderLeft' : 'borderTop']: `3px solid ${accentColor}`, ...style }
          : style
      }
      {...props}
    />
  )
);
StatCard.displayName = 'StatCard';
